import { useQuery, useMutation, useQueryClient, queryKeys } from './useQuery';
import { BookingApi } from '@/utils/api';
import type { ClinicConfigResponse, ApiService, Service } from '@/types/clinic';

/**
 * Custom hook for fetching clinic data by slug
 * Uses React Query for caching and background updates
 */
export const useClinicData = (slug: string) => {
  return useQuery({
    queryKey: queryKeys.clinic(slug),
    // Build minimal clinic info from config. No mocks.
    queryFn: async () => {
      const cfg = await BookingApi.getClinicConfigBySlug(slug);
      const cs = (cfg.active_theme?.theme?.color_scheme ||
        cfg.active_theme?.color_scheme) as unknown as Record<string, string>;
      const clinic = {
        id: String(cfg.location.id),
        name: cfg.location.name,
        slug: cfg.location.slug,
        description: cfg.ai_agent?.greeting_message || '',
        address: '',
        phone: '',
        email: '',
        website: undefined,
        theme: {
          primaryColor: cs?.primary || '',
          secondaryColor: cs?.secondary || '',
          accentColor: cs?.main || cs?.primary || '',
          backgroundColor: '#FFFFFF',
          textColor: cs?.['text-color'] || '#1F2937',
          mainColor: cs?.primary || '',
          gradient: (cs?.gradient as string) ?? null,
          iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
        },
        services: [] as Service[],
        doctors: [],
        workingHours: {
          monday: { isOpen: false },
          tuesday: { isOpen: false },
          wednesday: { isOpen: false },
          thursday: { isOpen: false },
          friday: { isOpen: false },
          saturday: { isOpen: false },
          sunday: { isOpen: false },
        },
        isActive: true,
        aiAgent: cfg.ai_agent,
      };
      return clinic;
    },
    enabled: !!slug, // Only run query if slug is provided
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

/**
 * Custom hook for fetching available appointment slots
 */
export const useAvailableSlots = (
  clinicId: string,
  doctorId: string,
  date: string
) => {
  return useQuery({
    queryKey: queryKeys.availableSlots(doctorId, date),
    // This hook is currently unused in pages; provide a no-op placeholder to satisfy types
    queryFn: async () => {
      return { success: true, slots: [], clinicId, doctorId, date } as const;
    },
    enabled: !!(clinicId && doctorId && date), // Only run if all params are provided
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes (slots change frequently)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

/**
 * Custom hook for creating appointments
 * Uses React Query mutation for optimistic updates and error handling
 */
export const useCreateBooking = () => {
  return useMutation({
    mutationFn: async () => {
      // No direct endpoint used elsewhere; keep placeholder if needed later
      throw new Error('createBooking is not implemented');
    },
    onSuccess: data => {
      console.log('Create booking success:', data);
    },
    onError: error => {
      console.error('Failed to create booking:', error);
      // You can add error handling logic here, like:
      // - Showing error toast
      // - Logging to error service
    },
  });
};

/**
 * Fetch raw clinic configuration for global access (booking_settings, active_theme, ai_agent, location)
 */
export const useClinicConfig = (slug: string) => {
  return useQuery<ClinicConfigResponse>({
    queryKey: queryKeys.clinicConfig(slug),
    queryFn: () => BookingApi.getClinicConfigBySlug(slug),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Custom hook for fetching clinic services
 * Uses React Query for caching and background updates
 */
export const useClinicServices = (slug: string) => {
  return useQuery({
    queryKey: queryKeys.clinicServices(slug),
    queryFn: async () => {
      const api = await BookingApi.getServices(slug);
      const services: Service[] = (api.data?.services || []).map(
        (s: ApiService): Service => ({
          id: String(s.id),
          name: s.name,
          description: s.description,
          duration: 0,
          price: parseFloat(s.pre_payment_amount) || 0,
          category: '',
          translated_name: s.translated_name,
          pre_payment_required: s.pre_payment_required,
          pre_payment_amount: s.pre_payment_amount,
          icon: s.icon
            ? s.icon.startsWith('http')
              ? s.icon
              : `https://core-api.elva.ai${s.icon}`
            : undefined,
        })
      );
      return { success: true, services, theme: api.data?.theme } as const;
    },
    enabled: !!slug,
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

/**
 * Custom hook for fetching clinic testimonials
 * Uses React Query for caching and background updates
 */
export const useClinicTestimonials = (slug: string) => {
  return useQuery({
    queryKey: queryKeys.clinicTestimonials(slug),
    queryFn: async () => {
      const data = await BookingApi.getTestimonials(slug);
      return { success: true, data } as const;
    },
    enabled: !!slug,
    staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes (testimonials change rarely)
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
  });
};

/**
 * Custom hook for fetching clinic providers for a specific service
 * Uses React Query for caching and background updates
 */
export const useClinicProviders = (slug: string, serviceId: string) => {
  return useQuery({
    queryKey: queryKeys.clinicProviders(slug, serviceId),
    queryFn: async () => {
      const res = await BookingApi.getProviders(slug, serviceId);
      const raw = res.providers || res.data?.providers || [];
      const providers = raw.map(p => ({
        id: String(p.id),
        name: (p as any).full_name || p.name || `Provider ${p.id}`,
        specialization: (p as any).specialty || p.specialization || '',
        experience: 0,
        education: '',
        image: (p as any).picture || p.image || undefined,
        bio: '',
        availableSlots: [],
      }));
      return { success: true, providers } as const;
    },
    enabled: !!slug && !!serviceId,
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

/**
 * Custom hook for fetching provider availability
 * Uses React Query for caching and background updates
 */
export const useProviderAvailability = (
  slug: string,
  providerId: string | number,
  startDate: string,
  endDate: string
) => {
  return useQuery({
    queryKey: queryKeys.availableSlots(providerId.toString(), startDate),
    queryFn: () =>
      BookingApi.getProviderAvailability(slug, providerId, {
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: !!slug && !!providerId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

/**
 * Utility hook to prefetch clinic data, services, and testimonials
 * Useful for preloading data when user hovers over links or navigates
 */
export const usePrefetchClinicData = () => {
  const queryClient = useQueryClient();

  const prefetchClinic = (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.clinic(slug),
      queryFn: async () => {
        const cfg = await BookingApi.getClinicConfigBySlug(slug);
        const cs = (cfg.active_theme?.theme?.color_scheme ||
          cfg.active_theme?.color_scheme) as unknown as Record<string, string>;
        return {
          id: String(cfg.location.id),
          name: cfg.location.name,
          slug: cfg.location.slug,
          description: cfg.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '',
            secondaryColor: cs?.secondary || '',
            accentColor: cs?.main || cs?.primary || '',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: cfg.ai_agent,
        };
      },
      staleTime: 10 * 60 * 1000,
    });
  };

  const prefetchServices = (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.clinicServices(slug),
      queryFn: async () => {
        const api = await BookingApi.getServices(slug);
        const services = (api.data?.services || []).map((s: ApiService) => ({
          id: String(s.id),
          name: s.name,
          description: s.description,
          duration: 0,
          price: parseFloat(s.pre_payment_amount) || 0,
          category: '',
          translated_name: s.translated_name,
          pre_payment_required: s.pre_payment_required,
          pre_payment_amount: s.pre_payment_amount,
          icon: s.icon
            ? s.icon.startsWith('http')
              ? s.icon
              : `https://core-api.elva.ai${s.icon}`
            : undefined,
        }));
        return { success: true, services, theme: api.data?.theme } as const;
      },
      staleTime: 15 * 60 * 1000,
    });
  };

  const prefetchTestimonials = (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.clinicTestimonials(slug),
      queryFn: async () => {
        const data = await BookingApi.getTestimonials(slug);
        return { success: true, data } as const;
      },
      staleTime: 30 * 60 * 1000,
    });
  };

  const prefetchAll = (slug: string) => {
    prefetchClinic(slug);
    prefetchServices(slug);
    prefetchTestimonials(slug);
  };

  return {
    prefetchClinic,
    prefetchServices,
    prefetchTestimonials,
    prefetchAll,
  };
};
